import { Alert, AlertDescription } from "@repo/ui/alert";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Progress } from "@repo/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/ui/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, DatabaseZap, FileUp, Loader2, UploadCloud } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { inferStateFromFileName } from "@/lib/valuations/sqmFileState";
import {
  backfillSqmToProperties,
  fetchCompletedChunksForFile,
  fetchSqmStagingAnalytics,
  fetchValuationIngressRuns,
  previewSqmImport,
  runSqmImport,
  type SqmBackfillResult,
  type SqmImportPreview,
  type SqmStagingFileSummary,
} from "@/lib/api";

const IMPORT_CHUNK_ROWS = 2_500;
const PREVIEW_BYTES = 512_000;

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCurrency(value: number | null | undefined): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(value ?? 0);
}

function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}


function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "completed") return "default";
  if (status === "failed") return "destructive";
  if (status === "running") return "outline";
  return "secondary";
}

interface CsvChunk {
  csvText: string;
  // 1-based CSV row number of the first data line in this chunk. The
  // parser adds a lineIndex 1..chunkSize per chunk; combined with this
  // offset (minus 1) we get globally unique row numbers per file,
  // preventing the historical collision where every chunk's rows were
  // numbered 1..chunkSize.
  baseRowNumber: number;
}

function splitCsvIntoChunks(csvText: string, chunkRows: number): CsvChunk[] {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length <= 1) {
    return [];
  }
  const header = lines[0];
  const chunks: CsvChunk[] = [];
  for (let index = 1; index < lines.length; index += chunkRows) {
    chunks.push({
      csvText: [header, ...lines.slice(index, index + chunkRows)].join("\n"),
      baseRowNumber: index,
    });
  }
  return chunks;
}

// NSW/QLD run ~227/236 chunks sequentially (566k/588k rows), each a
// separate edge-function round trip over several minutes. A single
// transient blip (cold start, dropped connection) used to abort the
// whole run, forcing a manual re-click to resume — harmless thanks to
// fetchCompletedChunksForFile's resume logic, but avoidable. Retry just
// the failing chunk a couple of times before giving up and surfacing it.
const TRANSIENT_IMPORT_ERROR = /timeout|timed out|network|fetch failed|failed to fetch|50[234]/i;

async function runSqmImportWithRetry(
  input: Parameters<typeof runSqmImport>[0],
  maxRetries = 2,
): Promise<Awaited<ReturnType<typeof runSqmImport>>> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await runSqmImport(input);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (attempt >= maxRetries || !TRANSIENT_IMPORT_ERROR.test(message)) {
        throw err;
      }
      console.warn(
        `[data-ingress] chunk ${input.chunkIndex} retry ${attempt + 1}/${maxRetries} after "${message}"`,
      );
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
}

const DataIngress = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<SqmImportPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    chunk: number;
    totalChunks: number;
    importedRows: number;
  } | null>(null);

  const {
    data: runs = [],
    error: runsError,
    isLoading: isLoadingRuns,
  } = useQuery({
    queryKey: ["valuation-ingress-runs"],
    queryFn: () => fetchValuationIngressRuns(50),
  });

  const selectedState = selectedFile ? inferStateFromFileName(selectedFile.name) : null;
  const progressPercent = importProgress
    ? Math.round((importProgress.chunk / importProgress.totalChunks) * 100)
    : 0;

  const handlePreview = async () => {
    if (!selectedFile) {
      return;
    }
    try {
      setIsPreviewing(true);
      const previewText = await selectedFile.slice(0, PREVIEW_BYTES).text();
      const nextPreview = await previewSqmImport({
        csvText: previewText,
        sourceFileName: selectedFile.name,
        sourceState: selectedState,
        maxRows: 25,
      });
      setPreview(nextPreview);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to preview SQM import";
      toast({ title: "Preview failed", description: message, variant: "destructive" });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setIsImporting(true);
      setImportProgress({ chunk: 0, totalChunks: 1, importedRows: 0 });
      const csvText = await selectedFile.text();
      const chunks = splitCsvIntoChunks(csvText, IMPORT_CHUNK_ROWS);
      if (chunks.length === 0) {
        throw new Error("CSV has no data rows to import");
      }

      // Resume from where the previous attempt left off. We query the
      // ingress-runs table directly (not the 50-row display cache) for
      // every chunk_index already marked `status='completed'` against
      // the same source_file_name AND the same total chunk count
      // (so we don't pick up chunk-indexes from an older differently-
      // sized version of the same filename). The next chunk to send is
      // the lowest index NOT in the set.
      const completedChunkSet = await fetchCompletedChunksForFile(selectedFile.name, chunks.length);
      const startIndex = chunks.findIndex((_, i) => !completedChunkSet.has(i + 1));
      const resumePoint = startIndex === -1 ? chunks.length : startIndex;
      if (resumePoint > 0) {
        toast({
          title: "Resuming SQM import",
          description: `Skipping ${resumePoint} chunk${resumePoint === 1 ? "" : "s"} already imported in a previous run. Continuing at chunk ${resumePoint + 1}/${chunks.length}.`,
        });
      }
      let importedRows = 0;
      setImportProgress({ chunk: resumePoint, totalChunks: chunks.length, importedRows: 0 });
      for (let index = resumePoint; index < chunks.length; index += 1) {
        // Skip chunks that got completed by another tab/session while
        // this loop was running (e.g. user retried mid-flight).
        if (completedChunkSet.has(index + 1)) {
          setImportProgress({ chunk: index + 1, totalChunks: chunks.length, importedRows });
          continue;
        }
        const run = await runSqmImportWithRetry({
          csvText: chunks[index].csvText,
          sourceFileName: selectedFile.name,
          sourceState: selectedState,
          chunkIndex: index + 1,
          totalChunks: chunks.length,
          baseRowNumber: chunks[index].baseRowNumber,
        });
        importedRows += run.imported_count;
        setImportProgress({
          chunk: index + 1,
          totalChunks: chunks.length,
          importedRows,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["valuation-ingress-runs"] });
      toast({
        title: "SQM import completed",
        description: `${importedRows.toLocaleString()} rows were imported/upserted from ${selectedFile.name} (resumed at chunk ${resumePoint + 1}).`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "SQM import failed";
      toast({ title: "Import failed", description: message, variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data Ingress</h1>
          <p className="text-muted-foreground">
            Import non-property-source data into valuation-specific tables without duplicating the
            property snapshot store.
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          SQM first
        </Badge>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          SQM files are imported into <span className="font-mono">sqm_sales_records</span>, not into{" "}
          <span className="font-mono">property_snapshots</span>. The importer sends chunks of{" "}
          {IMPORT_CHUNK_ROWS.toLocaleString()} rows per Edge Function call to avoid oversized
          requests.
        </AlertDescription>
      </Alert>

      {runsError ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {runsError instanceof Error
              ? runsError.message
              : "Unable to load valuation ingress run history."}
          </AlertDescription>
        </Alert>
      ) : null}

      <SqmStagingAnalyticsCard />

      <SqmBackfillCard />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5" />
              SQM back-history CSV import
            </CardTitle>
            <CardDescription>
              Expected columns: street_address, suburb, postcode, sold_price, sold_date, beds,
              baths, cars, area.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
              <div className="space-y-2">
                <Label htmlFor="sqm-file">CSV file</Label>
                <Input
                  id="sqm-file"
                  type="file"
                  accept=".csv,text/csv"
                  disabled={isImporting}
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    setSelectedFile(file);
                    setPreview(null);
                    setImportProgress(null);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Detected state</Label>
                <div className="flex h-10 items-center rounded-md border px-3 text-sm">
                  {selectedState ?? "Use ACT/NSW/NT/QLD/SA/TAS/VIC/WA.csv"}
                </div>
              </div>
            </div>

            {selectedFile ? (
              <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                <p className="font-medium">{selectedFile.name}</p>
                <p className="text-muted-foreground">
                  {formatBytes(selectedFile.size)} · imported in browser-managed chunks.
                </p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!selectedFile || isPreviewing || isImporting}
                onClick={handlePreview}
              >
                {isPreviewing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <DatabaseZap className="mr-2 h-4 w-4" />
                )}
                Preview
              </Button>
              <Button type="button" disabled={!selectedFile || isImporting} onClick={handleImport}>
                {isImporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UploadCloud className="mr-2 h-4 w-4" />
                )}
                Import SQM rows
              </Button>
            </div>

            {importProgress ? (
              <div className="space-y-2 rounded-lg border p-4">
                <div className="flex items-center justify-between text-sm">
                  <span>
                    Chunk {importProgress.chunk} / {importProgress.totalChunks}
                  </span>
                  <span>{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} />
                <p className="text-sm text-muted-foreground">
                  {importProgress.importedRows.toLocaleString()} rows imported/upserted so far.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Preview summary</CardTitle>
            <CardDescription>Validate parsing before importing a large state file.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {preview ? (
              <>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg border p-3">
                    <p className="text-xl font-semibold">{preview.totalRowsRead}</p>
                    <p className="text-xs text-muted-foreground">Rows read</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xl font-semibold">{preview.rejectedRows}</p>
                    <p className="text-xs text-muted-foreground">Rejected</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xl font-semibold">{preview.warningCount}</p>
                    <p className="text-xs text-muted-foreground">Warnings</p>
                  </div>
                </div>
                {preview.warnings.length > 0 ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    {preview.warnings.join("; ")}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select a CSV and run Preview to see sample parsed rows and warning counts.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Parsed sample rows</CardTitle>
          <CardDescription>
            First rows from the selected file. Blank prices are allowed but flagged.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Suburb</TableHead>
                  <TableHead>Postcode</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Segment</TableHead>
                  <TableHead>Warnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!preview || preview.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No preview rows loaded.
                    </TableCell>
                  </TableRow>
                ) : null}
                {preview?.rows.map((row) => (
                  <TableRow key={`${row.rowNumber}-${row.saleEventKey}`}>
                    <TableCell className="min-w-[220px]">{row.streetAddress}</TableCell>
                    <TableCell>{row.suburb}</TableCell>
                    <TableCell>{row.postcode}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.soldPrice)}</TableCell>
                    <TableCell>{row.soldDate ?? "—"}</TableCell>
                    <TableCell>
                      {row.beds ?? "?"}b / {row.baths ?? "?"}ba / {row.cars ?? "?"}c
                    </TableCell>
                    <TableCell>
                      {row.parseWarnings.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {row.parseWarnings.map((warning) => (
                            <Badge key={`${row.rowNumber}-${warning}`} variant="secondary">
                              {warning.replace(/_/g, " ")}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ingress run history</CardTitle>
          <CardDescription>
            Chunk-level import history for auditing and retry decisions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Imported</TableHead>
                  <TableHead className="text-right">Rejected</TableHead>
                  <TableHead className="text-right">Warnings</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingRuns ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      Loading ingress runs...
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoadingRuns && runs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No ingress runs yet.
                    </TableCell>
                  </TableRow>
                ) : null}
                {runs.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell>{run.valuation_data_sources?.name ?? "Valuation source"}</TableCell>
                    <TableCell>
                      <div>
                        <p>{run.source_file_name ?? "—"}</p>
                        {run.total_chunks ? (
                          <p className="text-xs text-muted-foreground">
                            Chunk {run.chunk_index ?? "?"} / {run.total_chunks}
                          </p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {run.imported_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {run.rejected_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {run.warning_count.toLocaleString()}
                    </TableCell>
                    <TableCell>{formatDate(run.completed_at ?? run.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Per-file SQM staging analytics. Shows rows staged + duplicate sale-event
 * groups + missing-field rates so the operator can spot data quality
 * issues without digging into raw rows. Reads from the
 * sqm-staging-analytics edge action.
 */
const SqmStagingAnalyticsCard = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["sqm-staging-analytics"],
    queryFn: () => fetchSqmStagingAnalytics(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DatabaseZap className="h-5 w-5" />
          Staged SQM data
        </CardTitle>
        <CardDescription>
          Per-file totals from <span className="font-mono">sqm_sales_records</span>. Duplicate
          sale-event groups are rows that share the same{" "}
          <span className="font-mono">sale_event_key</span> (state + address + suburb + postcode +
          date + price) — these are preserved as provenance and deduped at valuation calc time. The{" "}
          <span className="font-mono">Backfilled</span> column counts rows that landed in{" "}
          <span className="font-mono">property_snapshots</span> via{" "}
          <span className="font-mono">backfill_sqm_to_properties</span>; the gap to{" "}
          <span className="font-mono">Rows</span> is the <span className="font-mono">No price</span>{" "}
          stragglers (intentional exclusions, not bugs).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading staging analytics…
          </div>
        ) : isError ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : "Unable to load staging analytics."}
            </AlertDescription>
          </Alert>
        ) : !data || data.files.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No staged SQM data yet. Upload a state CSV via the import card below.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Rows</TableHead>
                <TableHead className="text-right">Backfilled</TableHead>
                <TableHead className="text-right">Unique events</TableHead>
                <TableHead className="text-right">Dup groups</TableHead>
                <TableHead className="text-right">Dup extra</TableHead>
                <TableHead className="text-right">No price</TableHead>
                <TableHead className="text-right">No date</TableHead>
                <TableHead className="text-right">No beds</TableHead>
                <TableHead className="text-right">No baths</TableHead>
                <TableHead className="text-right">No cars</TableHead>
                <TableHead className="text-right">No area</TableHead>
                <TableHead>Latest ingress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.files.map((file: SqmStagingFileSummary) => (
                <TableRow key={`${file.source_file_name}|${file.source_state ?? ""}`}>
                  <TableCell className="font-mono text-xs">{file.source_file_name}</TableCell>
                  <TableCell>
                    {file.source_state ? (
                      <Badge variant="outline">{file.source_state}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{file.total_rows.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {file.backfilled_count.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {file.unique_sale_events.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {file.duplicate_sale_event_groups.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {file.duplicate_extra_rows.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {file.missing_sold_price.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {file.missing_sold_date.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">{file.missing_beds.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {file.missing_baths.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">{file.missing_cars.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {file.missing_area_sqm.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(file.latest_ingress_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * SQM → properties backfill control. Adapter_slug="sqm-back-history",
 * idempotent by source_url. See
 * .cursor/plans/sqm-import-reliability-and-backfill.md.
 */
const SqmBackfillCard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [state, setState] = useState<string>("");
  const [limit, setLimit] = useState<number>(10_000);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState<SqmBackfillResult | null>(null);

  const trigger = async (dryRun: boolean) => {
    setLoading(true);
    try {
      const result = await backfillSqmToProperties({
        state: state.trim() ? state.trim().toUpperCase() : null,
        limit,
        dryRun,
      });
      setLastResult(result);
      toast({
        title: dryRun ? "Backfill preview" : "Backfill complete",
        description: `${result.snapshotsInserted} snapshots, ${result.propertiesUpserted} properties (scope: ${result.scopedCount.toLocaleString()})`,
      });
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: ["valuation-ingress-runs"] });
        queryClient.invalidateQueries({ queryKey: ["valuation-data-sources"] });
      }
    } catch (err) {
      toast({
        title: "Backfill failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DatabaseZap className="h-5 w-5" />
          SQM → main database backfill
        </CardTitle>
        <CardDescription>
          Promote staged SQM sales into <span className="font-mono">properties</span> +{" "}
          <span className="font-mono">property_snapshots</span> under{" "}
          <span className="font-mono">adapter_slug=&apos;sqm-back-history&apos;</span>. Every SQM
          address gets a new snapshot (provenance preserved in{" "}
          <span className="font-mono">raw_data</span>). Idempotent — re-runs skip rows already
          snapshotted via <span className="font-mono">source_url</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="sqm-backfill-state">State (optional)</Label>
            <Input
              id="sqm-backfill-state"
              placeholder="e.g. VIC"
              value={state}
              onChange={(e) => setState(e.target.value)}
              disabled={loading}
              maxLength={3}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sqm-backfill-limit">Batch limit</Label>
            <Input
              id="sqm-backfill-limit"
              type="number"
              min={100}
              max={50_000}
              value={limit}
              onChange={(e) =>
                setLimit(Math.max(100, Math.min(50_000, Number(e.target.value) || 10_000)))
              }
              disabled={loading}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => trigger(true)}
              disabled={loading}
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Dry-run
            </Button>
            <Button type="button" onClick={() => trigger(false)} disabled={loading}>
              Run backfill
            </Button>
          </div>
        </div>
        {lastResult ? (
          <div className="rounded-md border bg-muted/30 p-3 text-xs grid gap-1 sm:grid-cols-3">
            <div>
              <span className="text-muted-foreground">Mode:</span>{" "}
              <span className="font-medium">{lastResult.dryRun ? "dry-run" : "applied"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Scope (total):</span>{" "}
              <span className="font-medium">{lastResult.scopedCount.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Considered (this batch):</span>{" "}
              <span className="font-medium">{lastResult.consideredCount.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Properties upserted:</span>{" "}
              <span className="font-medium">{lastResult.propertiesUpserted.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Snapshots inserted:</span>{" "}
              <span className="font-medium text-foreground">
                {lastResult.snapshotsInserted.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Skipped (already done):</span>{" "}
              <span className="font-medium">
                {lastResult.skippedAlreadySnapshotted.toLocaleString()}
              </span>
            </div>
            {lastResult.skippedInvalidAddress > 0 ? (
              <div className="sm:col-span-3 text-muted-foreground">
                Skipped {lastResult.skippedInvalidAddress} rows with un-normalisable addresses.
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default DataIngress;
